import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import {
  ButtonOBSOLETED,
  Notification,
  Message,
} from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import {
  Filters,
  FilterInput,
  FilterSelect,
} from "./Filters";
import { getQueryStringParameters } from "../../common/utils";
import { DisplayGuid, DisplayDate } from "../items";
import AddEntityButton from "../misc/AddEntityButton";

const FacilityRedirectsInternal = (props) => {
  const { history, location } = props;
  const [loadedData, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const navigationButtonStyle = {
    width: "6%",
    color: "grey",
    fontSize: "0.85rem",
  };

  const clickable = {
    cursor: "pointer"
  };

  useEffect(() => {
    async function loadFromApi() {
      loadData();
    }
    if (!loadedData) {
      loadFromApi();
    }
  });

  const loadData = async () => {
    setIsLoading(false);
    const filters = getQueryStringParameters(location.search);
    try {
      const data = await props.api.facilityRedirects.searchFacilityRedirects({
        filters
      });
      setData(data);
      setIsLoading(true);
    } catch (e) {
      console.error('Error loading facility redirects:', e);
      setData({ redirects: [], pages: 0, totalCount: 0 });
      setIsLoading(true);
    }
  };

  const filters = getQueryStringParameters(location.search);
  const page = parseInt(filters.page) || 1;
  const orderBy = filters.orderBy || "CreatedAt";
  const orderDirection = filters.orderDirection || "desc";

  const orderHandler = (name) => {
    let direction;
    if (name.toLowerCase() === orderBy.toLowerCase()) {
      direction = orderDirection === "desc" ? "asc" : "desc";
    } else {
      direction = "asc";
    }

    Filters.goToPage(history, location, page, name, direction);
  };

  const pagingHandler = async (action) => {
    if (page < loadedData.pages && action === "add") {
      Filters.goToPage(history, location, page + 1, orderBy, orderDirection);
    }
    if (page > 1 && action === "sub") {
      Filters.goToPage(history, location, page - 1, orderBy, orderDirection);
    }
  };

  const mappedRedirectTypes = [
    { value: '1', label: 'Facility Slug Rename' },
    { value: '2', label: 'Product Slug Rename' },
    { value: '3', label: 'Full URL Redirect' }
  ];

  const mappedIsActive = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const getRedirectTypeLabel = (type) => {
    switch(type) {
      case 'FacilitySlugRename':
        return 'Facility Slug';
      case 'ProductSlugRename':
        return 'Product Slug';
      case 'FullUrlRedirect':
        return 'Full URL';
      default:
        return type;
    }
  };

  const header = [
    {
      name: "Id",
      label: "Id",
      canOrder: true,
      breakWord: false,
      render: ({ id }) => <DisplayGuid baseAnchor={'facility-redirect'}>{id}</DisplayGuid>,
    },
    {
      name: "SourceSlug",
      label: "Source Slug",
      canOrder: true,
      breakWord: true,
      render: ({ sourceSlug }) => sourceSlug,
    },
    {
      name: "TargetSlug",
      label: "Target Slug",
      canOrder: true,
      breakWord: true,
      render: ({ targetSlug }) => targetSlug || '-',
    },
    {
      name: "TargetUrl",
      label: "Target URL",
      canOrder: false,
      breakWord: true,
      render: ({ targetUrl }) => targetUrl || '-',
    },
    {
      name: "Type",
      label: "Type",
      canOrder: true,
      breakWord: false,
      render: ({ type }) => getRedirectTypeLabel(type),
    },
    {
      name: "ProductSlug",
      label: "Product Slug",
      canOrder: false,
      breakWord: false,
      render: ({ productSlug }) => productSlug || '-',
    },
    {
      name: "IsActive",
      label: "Status",
      canOrder: true,
      breakWord: false,
      render: ({ isActive }) => (
        <span style={{ color: isActive ? 'green' : 'red', fontWeight: 'bold' }}>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "CreatedAt",
      label: "Created (UTC)",
      canOrder: true,
      breakWord: false,
      render: ({ createdAt }) => (
        <DisplayDate breakTime>{createdAt}</DisplayDate>
      ),
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <PageTop
            documentTitle="Facility Redirects"
            documentDescription="Manage facility URL redirects"
          />

          <h2 className="main-page-header">
            Facility Redirects
            <AddEntityButton to='/facility-redirect' />
          </h2>

          <Filters
            initFromUrl
            submitUrl
            validateForm={(
              {
                id,
                sourceSlug,
                targetSlug,
                targetUrl,
                type,
                productSlug,
                isActive
              },
              updatedKey
            ) => {
              if (updatedKey !== "id") {
                return {
                  id,
                  sourceSlug,
                  targetSlug,
                  targetUrl,
                  type,
                  productSlug,
                  isActive
                };
              }
              return { id };
            }}
            render={(formData, update) => (
              <div className="filters-block">
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="id">
                    Redirect Id
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="id"
                    placeholder="Find by id"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="sourceSlug">
                    Source Slug
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="sourceSlug"
                    placeholder="Find by source slug"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="targetSlug">
                    Target Slug
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="targetSlug"
                    placeholder="Find by target slug"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="targetUrl">
                    Target URL
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="targetUrl"
                    placeholder="Find by target URL"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="type">
                    Redirect Type
                  </label>
                  <FilterSelect
                    className='filter-select'
                    data={formData}
                    update={update}
                    name='type'
                    options={mappedRedirectTypes}
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="productSlug">
                    Product Slug
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="productSlug"
                    placeholder="Find by product slug"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="isActive">
                    Status
                  </label>
                  <FilterSelect
                    className='filter-select'
                    data={formData}
                    update={update}
                    name='isActive'
                    options={mappedIsActive}
                  />
                </div>
                <br />
                <br />
                <div className="filters-submit-wrapper">
                  <ButtonOBSOLETED
                    type="submit"
                    buttonContent="Search"
                    disabled={!isLoading}
                  />
                </div>
                <hr />
              </div>
            )}
          />

          {loadedData == null ? (
            <span>Loading...</span>
          ) : (
            <>
              {loadedData.redirects && loadedData.redirects.length > 0 ? (
                <React.Fragment>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="< Prev"
                      onClick={() => pagingHandler("sub")}
                    />
                    <span style={{ color: "grey", fontSize: "0.85rem" }}>
                      Showing page {page} out of {loadedData.pages}
                    </span>
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="Next >"
                      onClick={() => pagingHandler("add")}
                    />
                  </div>
                  <br />

                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table cell-content-align-top">
                      <thead>
                        <tr>
                          {header.map((property) => (
                            <td
                              key={property.name}
                              style={property.canOrder ? clickable : null}
                              onClick={() =>
                                property.canOrder
                                  ? orderHandler(property.name)
                                  : null
                              }
                            >
                              {property.label}{" "}
                              {orderBy.toLowerCase() === property.name.toLowerCase()
                                ? orderDirection === "desc"
                                  ? "↓"
                                  : "↑"
                                : ""}
                            </td>
                          ))}
                        </tr>
                      </thead>
                      {loadedData.redirects.map((redirect) => (
                        <tbody key={redirect.id}>
                          <tr className="hover-listview ">
                            {header.map((property) => (
                              <td key={property.name} style={property.breakWord ? {overflowWrap: 'anywhere'} : {}}>
                                <strong className="table-cell-label">
                                  {property.label}
                                </strong>
                                {property.render(redirect)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      ))}
                    </table>
                  </article>

                  <div
                    style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}
                  >
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="< Prev"
                      onClick={() => pagingHandler("sub")}
                    />
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="Next >"
                      onClick={() => pagingHandler("add")}
                    />
                  </div>
                </React.Fragment>
              ) : (
                <div style={{ padding: "20px", textAlign: "center", color: "grey" }}>
                  No facility redirects found matching your search criteria.
                </div>
              )}
            </>
          )}
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

FacilityRedirectsInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
};

const FacilityRedirects = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(FacilityRedirectsInternal)
  )
);

FacilityRedirects.requiresLogin = true;

export default FacilityRedirects;
